import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { 
  CreditCard, 
  Check, 
  Loader2, 
  Zap, 
  Building2,
  Calendar,
  Sparkles,
  Coins,
  HandCoins
} from "lucide-react";
import { toast } from "sonner";
import { formatCurrency, type Currency } from "@/lib/currency";

interface Props {
  merchantId: string;
  currentPlan: string;
  onSuccess: () => void;
  currency?: Currency;
  isProfessional?: boolean;
}

// Merchant (Shopify/WooCommerce) plans
const MERCHANT_PLANS = [
  {
    id: "payg",
    name: "Pay As You Go",
    description: "Start with minimal commitment",
    monthlyPrice: 100,
    annualPrice: 1000,
    features: ["Unlimited splits", "2% per transaction", "Email support"],
    icon: Sparkles,
    popular: false,
    isPayg: true,
  },
  {
    id: "starter",
    name: "Starter",
    description: "For growing businesses",
    monthlyPrice: 499,
    annualPrice: 4990,
    features: ["50 splits/month", "1.5% per transaction", "Email support"],
    icon: Zap,
    popular: false,
    isPayg: false,
  },
  {
    id: "growth",
    name: "Growth",
    description: "For scaling merchants",
    monthlyPrice: 1999,
    annualPrice: 19990,
    features: ["Unlimited splits", "1% per transaction", "Priority support", "API access"],
    icon: Zap,
    popular: true,
    isPayg: false,
  },
  {
    id: "enterprise",
    name: "Enterprise",
    description: "For large operations",
    monthlyPrice: 9999,
    annualPrice: 99990,
    features: ["Everything in Growth", "White-label", "Dedicated manager", "Custom integrations"],
    icon: Building2,
    popular: false,
    isPayg: false,
  },
];

// Professional (Coaches, Tutors, etc.) plans
const PROFESSIONAL_PLANS = [
  {
    id: "professional_payperuse",
    name: "Pay Per Use",
    description: "Only pay when you collect",
    monthlyPrice: 0,
    annualPrice: 0,
    perCollectionFee: 10,
    features: ["Unlimited collections", "₹10 per payment collected", "Email notifications", "Automated reminders"],
    icon: Coins,
    popular: false,
    isPayPerUse: true,
  },
  {
    id: "professional_starter",
    name: "Starter",
    description: "For regular collections",
    monthlyPrice: 500,
    annualPrice: 4800, // 20% discount
    features: ["60 collections/month", "All features included", "Priority email support", "Analytics dashboard"],
    icon: Zap,
    popular: true,
    isPayPerUse: false,
  },
  {
    id: "professional_growth",
    name: "Growth",
    description: "For high-volume professionals",
    monthlyPrice: 1000,
    annualPrice: 9600, // 20% discount
    features: ["120 collections/month", "Everything in Starter", "Priority support (24h)", "Advanced analytics"],
    icon: HandCoins,
    popular: false,
    isPayPerUse: false,
  },
];

declare global {
  interface Window {
    Razorpay: any;
  }
}

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
export function SubscriptionPayment({ 
  merchantId, 
  currentPlan, 
  onSuccess, 
  currency = 'INR',
  isProfessional = false 
}: Props) {
  const PLANS = isProfessional ? PROFESSIONAL_PLANS : MERCHANT_PLANS;
  const defaultPlan = isProfessional ? "professional_starter" : "growth";
  
  const [selectedPlan, setSelectedPlan] = useState<string>(defaultPlan);
  const [billingCycle, setBillingCycle] = useState<"monthly" | "annual">("monthly");
  const [processing, setProcessing] = useState(false);

  const selectedPlanData = PLANS.find(p => p.id === selectedPlan);
  const isPayPerUse = (selectedPlanData as any)?.isPayPerUse || false;
  const isPayg = (selectedPlanData as any)?.isPayg || false;
  const price = billingCycle === "annual" 
    ? selectedPlanData?.annualPrice || 0
    : selectedPlanData?.monthlyPrice || 0;

  const annualSavings = selectedPlanData 
    ? (selectedPlanData.monthlyPrice * 12) - selectedPlanData.annualPrice
    : 0;

  const loadRazorpayScript = (): Promise<boolean> => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayment = async () => {
    // Pay Per Use doesn't require upfront payment
    if (isPayPerUse) {
      toast.info("Pay Per Use", {
        description: "You'll be charged ₹10 for each payment you collect. No upfront payment needed!",
      });
      return;
    }

    setProcessing(true);

    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        throw new Error("Failed to load payment gateway");
      }

      // Create order
      const orderResponse = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-subscription-order`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            merchantId,
            plan: selectedPlan,
            billingCycle,
          }),
        }
      );

      const orderData = await orderResponse.json();

      if (!orderResponse.ok) {
        throw new Error(orderData.error || "Failed to create order");
      }

      // Open Razorpay checkout
      const options = {
        key: orderData.keyId,
        amount: orderData.amount,
        currency: orderData.currency,
        name: "Shakatah",
        description: orderData.planName,
        order_id: orderData.orderId,
        handler: async (response: any) => {
          try {
            // Verify payment
            const verifyResponse = await fetch(
              `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/verify-subscription-payment`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                  merchantId,
                  plan: selectedPlan,
                  billingCycle,
                }),
              }
            );

            const verifyData = await verifyResponse.json();

            if (!verifyResponse.ok) {
              throw new Error(verifyData.error || "Payment verification failed");
            }

            toast.success("Subscription activated!", {
              description: `You're now on the ${selectedPlanData?.name} plan`,
            });
            onSuccess();
          } catch (error: any) {
            console.error("Verification error:", error);
            toast.error("Payment verification failed", {
              description: "Please contact support if amount was deducted",
            });
          }
        },
        prefill: {
          name: orderData.storeName,
        },
        theme: {
          color: isProfessional ? "#0891B2" : "#8B5CF6", // Cyan for professional, Purple for merchant
        },
        modal: {
          ondismiss: () => {
            setProcessing(false);
          },
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error: any) {
      console.error("Payment error:", error);
      toast.error(error.message || "Failed to initiate payment");
      setProcessing(false);
    }
  };

  const themeColor = isProfessional ? "cyan" : "primary";

  return (
    <div className="space-y-6">
      {/* Free Period Banner for Professionals */}
      {isProfessional && currentPlan?.includes('trial') && (
        <div className="bg-teal-500/10 border border-teal-500/20 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-500/20 rounded-lg">
              <Sparkles className="h-5 w-5 text-teal-600" />
            </div>
            <div>
              <p className="font-medium text-teal-700">You're on the Free Period</p>
              <p className="text-sm text-teal-600">
                Enjoy unlimited collections for 3 months. Choose a plan below when you're ready.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Billing Cycle Toggle - hide for Pay Per Use when selected */}
      {!isPayPerUse && (
        <div className={`flex items-center justify-center gap-4 p-4 rounded-lg ${isProfessional ? 'bg-cyan-50 dark:bg-cyan-950/30' : 'bg-muted'}`}>
          <RadioGroup
            value={billingCycle}
            onValueChange={(v) => setBillingCycle(v as "monthly" | "annual")}
            className="flex gap-4"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="monthly" id="monthly" />
              <Label htmlFor="monthly">Monthly</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="annual" id="annual" />
              <Label htmlFor="annual" className="flex items-center gap-2">
                Annual
                <Badge variant="secondary" className="bg-green-500/10 text-green-600">
                  Save 2 months
                </Badge>
              </Label>
            </div>
          </RadioGroup>
        </div>
      )}

      {/* Plan Selection */}
      <div className="grid gap-4">
        {PLANS.map((plan) => {
          const isSelected = selectedPlan === plan.id;
          const isCurrent = currentPlan === plan.id || 
            (currentPlan?.includes('trial') && plan.id.includes('trial'));
          const displayPrice = billingCycle === "annual" ? plan.annualPrice : plan.monthlyPrice;
          const planIsPayPerUse = (plan as any).isPayPerUse;
          const perCollectionFee = (plan as any).perCollectionFee;

          return (
            <Card
              key={plan.id}
              className={`cursor-pointer transition-all ${
                isSelected
                  ? isProfessional 
                    ? "border-cyan-500 ring-2 ring-cyan-500/20"
                    : "border-primary ring-2 ring-primary/20"
                  : isProfessional
                    ? "hover:border-cyan-500/50"
                    : "hover:border-primary/50"
              } ${isCurrent ? isProfessional ? "bg-cyan-500/5" : "bg-primary/5" : ""}`}
              onClick={() => setSelectedPlan(plan.id)}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-lg ${
                      isSelected 
                        ? isProfessional 
                          ? "bg-cyan-600 text-white" 
                          : "bg-primary text-primary-foreground" 
                        : isProfessional
                          ? "bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600"
                          : "bg-primary/10 text-primary"
                    }`}>
                      <plan.icon className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold">{plan.name}</h3>
                        {plan.popular && (
                          <Badge className={isProfessional ? "bg-cyan-500/10 text-cyan-600" : "bg-primary/10 text-primary"}>
                            Popular
                          </Badge>
                        )}
                        {isCurrent && (
                          <Badge variant="outline">Current</Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">{plan.description}</p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {plan.features.map((f) => (
                          <span key={f} className="text-xs bg-muted px-2 py-1 rounded">
                            {f}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    {planIsPayPerUse ? (
                      <>
                        <div className="text-2xl font-bold">
                          ₹{perCollectionFee}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          per collection
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="text-2xl font-bold">
                          {formatCurrency(displayPrice, currency)}
                          {(plan as any).isPayg && <span className="text-sm font-normal text-muted-foreground"> +2%</span>}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          /{billingCycle === "annual" ? "year" : "month"}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Payment Summary */}
      <Card className={isProfessional ? "bg-cyan-500/5 border-cyan-500/20" : "bg-primary/5 border-primary/20"}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="font-medium">{selectedPlanData?.name} Plan</p>
              <p className="text-sm text-muted-foreground">
                {isPayPerUse 
                  ? "No monthly fee - pay only when you collect" 
                  : billingCycle === "annual" 
                    ? "Annual billing" 
                    : "Monthly billing"}
                {isPayg && " + 2% per transaction"}
              </p>
            </div>
            <div className="text-right">
              {isPayPerUse ? (
                <>
                  <p className="text-2xl font-bold">₹10</p>
                  <p className="text-sm text-muted-foreground">per collection</p>
                </>
              ) : (
                <>
                  <p className="text-2xl font-bold">{formatCurrency(price, currency)}</p>
                  {billingCycle === "annual" && annualSavings > 0 && !isPayg && (
                    <p className="text-sm text-green-600">
                      Save {formatCurrency(annualSavings, currency)}/year
                    </p>
                  )}
                  {isPayg && (
                    <p className="text-xs text-muted-foreground">
                      + 2% of each transaction
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
          <Button
            onClick={handlePayment}
            disabled={processing || (currentPlan === selectedPlan && !isPayPerUse)}
            className={`w-full ${isProfessional ? 'bg-cyan-600 hover:bg-cyan-700' : ''}`}
            size="lg"
          >
            {processing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : isPayPerUse ? (
              <>
                <Check className="h-4 w-4 mr-2" />
                Activate Pay Per Use
              </>
            ) : currentPlan === selectedPlan ? (
              <>
                <Check className="h-4 w-4 mr-2" />
                Current Plan
              </>
            ) : (
              <>
                <CreditCard className="h-4 w-4 mr-2" />
                Pay with Razorpay
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};
